import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Canvas, useFrame } from '@react-three/fiber';
import { Environment, Float, ContactShadows } from '@react-three/drei';
import { motion } from 'framer-motion';

// --- 3D Components ---

const FloatingRing = () => {
  const meshRef = useRef();

  useFrame((state, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.x += delta * 0.2;
      meshRef.current.rotation.y += delta * 0.3;
    }
  });

  return (
    <Float floatIntensity={2} speed={1.5}>
      <mesh ref={meshRef} position={[0, 0, 0]} scale={1.5}>
        <torusGeometry args={[1, 0.2, 32, 64]} />
        <meshPhysicalMaterial 
          color="#b76e79"
          metalness={1}
          roughness={0.15}
          clearcoat={1}
          clearcoatRoughness={0.1}
        />
      </mesh>
    </Float>
  );
};

const FloatingDiamond = ({ position, scale }) => {
  const meshRef = useRef();
  
  useFrame((state, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.y += delta * 0.5;
      meshRef.current.rotation.x += delta * 0.1;
    }
  });

  return (
    <Float floatIntensity={2} speed={2}>
      <mesh ref={meshRef} position={position} scale={scale}>
        <octahedronGeometry args={[1, 0]} />
        <meshPhysicalMaterial 
          color="#ffffff"
          transmission={1}
          opacity={1}
          metalness={0}
          roughness={0}
          ior={2.4}
          thickness={0.5}
        />
      </mesh>
    </Float>
  );
};

const SceneSetup = () => {
  const groupRef = useRef();
  const targetRotation = useRef(0);
  const targetY = useRef(0);
  const targetZ = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      // Calculate scroll progress (0 to roughly 3 for 4 pages)
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const progress = Math.max(0, Math.min(1, window.scrollY / maxScroll));
      
      // Update target values based on progress
      targetRotation.current = progress * Math.PI * 2;
      targetY.current = -progress * 5;
      targetZ.current = progress * 2;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Initial call
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useFrame((state, delta) => {
    if (groupRef.current) {
      // Lerp for smooth camera movement
      groupRef.current.rotation.y += (targetRotation.current - groupRef.current.rotation.y) * delta * 5;
      groupRef.current.position.y += (targetY.current - groupRef.current.position.y) * delta * 5;
      groupRef.current.position.z += (targetZ.current - groupRef.current.position.z) * delta * 5;
    }
  });

  return (
    <group ref={groupRef}>
      <FloatingRing />
      <FloatingDiamond position={[2, 2, -2]} scale={0.5} />
      <FloatingDiamond position={[-2, -1, -1]} scale={0.4} />
      <FloatingDiamond position={[1.5, -2, -3]} scale={0.6} />
    </group>
  );
};

// --- Main Page Component ---
const HomePage = () => {
  // Ensure window starts at top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-dark text-white font-sans">
      
      {/* Fixed 3D Canvas Background */}
      <div className="fixed top-0 left-0 w-full h-full z-0 pointer-events-none">
        <Canvas camera={{ position: [0, 0, 5], fov: 45 }}>
          <color attach="background" args={['#111111']} />
          <ambientLight intensity={0.5} />
          <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} intensity={1} castShadow />
          <pointLight position={[-10, -10, -10]} intensity={0.5} />
          <Environment preset="city" />
          <SceneSetup />
          <ContactShadows position={[0, -2.5, 0]} opacity={0.4} scale={10} blur={2} far={4} />
        </Canvas>
      </div>

      {/* Scrolling HTML Content */}
      <div className="relative z-10 w-full">
        
        {/* Page 1: Hero */}
        <section className="w-full min-h-screen flex flex-col items-center justify-center pointer-events-none px-4">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
            className="text-5xl sm:text-7xl font-serif text-gold mb-4 text-center tracking-wide"
          >
            LuxeJewels
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.6 }}
            className="text-gray-300 text-lg sm:text-xl font-light tracking-widest uppercase text-center"
          >
            The Art of Timeless Elegance
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white/50 text-sm tracking-widest flex flex-col items-center gap-2"
          >
            <div className="w-[1px] h-12 bg-gradient-to-b from-gold to-transparent mb-2"></div>
            Scroll to Discover
          </motion.div>
        </section>

        {/* Page 2: Storytelling */}
        <section className="w-full min-h-screen flex items-center justify-start px-[10%] pointer-events-none">
          <div className="max-w-md bg-dark/20 p-8 rounded-xl backdrop-blur-sm border border-gold/10">
            <motion.h2 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1 }}
              className="text-4xl sm:text-5xl font-serif text-white mb-6"
            >
              Heritage & <span className="text-gold italic">Craft</span>
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, delay: 0.3 }}
              className="text-gray-400 text-lg leading-relaxed"
            >
              Each piece is meticulously crafted by master jewelers, transforming ethically sourced precious metals and stones into radiant works of art that echo through generations.
            </motion.p>
          </div>
        </section>

        {/* Page 3: Service Promise */}
        <section className="w-full min-h-screen flex items-center justify-end px-[10%] text-right pointer-events-none">
          <div className="max-w-md bg-dark/20 p-8 rounded-xl backdrop-blur-sm border border-gold/10">
            <motion.h2 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1 }}
              className="text-4xl sm:text-5xl font-serif text-white mb-6"
            >
              The <span className="text-gold italic">Promise</span>
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, delay: 0.3 }}
              className="text-gray-400 text-lg leading-relaxed"
            >
              Unparalleled white-glove service. From our vault to your door, fully insured, with guaranteed real-time tracking. Because luxury is an experience.
            </motion.p>
          </div>
        </section>

        {/* Page 4: CTA */}
        <section className="w-full min-h-screen flex flex-col items-center justify-center text-center px-4">
          <div className="bg-dark/40 p-12 rounded-2xl backdrop-blur-md border border-gold/20">
            <motion.h2 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="text-4xl sm:text-6xl font-serif text-white mb-8"
            >
              Begin Your <span className="text-gold italic">Journey</span>
            </motion.h2>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.4 }}
              className="pointer-events-auto flex flex-col sm:flex-row gap-4 items-center justify-center"
            >
              <Link to="/products" className="px-12 py-4 bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-dark transition-all duration-300 font-bold tracking-widest uppercase text-sm w-full sm:w-auto">
                Explore Collection
              </Link>
              <Link to="/about" className="px-12 py-4 bg-transparent border-2 border-white text-white hover:bg-white hover:text-dark transition-all duration-300 font-bold tracking-widest uppercase text-sm w-full sm:w-auto">
                Our Legacy
              </Link>
            </motion.div>
          </div>
        </section>
        
      </div>
    </div>
  );
};

export default HomePage;
